import type React from "react";
import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { AgGridReact } from "ag-grid-react";
import type {
	ColDef,
	GridApi,
	GridReadyEvent,
	SelectionChangedEvent,
	FirstDataRenderedEvent,
} from "ag-grid-community";
import { TextField } from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import { ErrorModal } from "@dexteel/mesf-core";
import LazyLoading from "@/components/loaders/LazyLoading/LazyLoading";
import type { AssetDocument } from "@/pages/asset-details/models/AssetDocument";
import { globalDefaultColDef } from "@setup/ag-grid/mesf-theme";

interface Props {
	allDocuments: AssetDocument[];
	assetDocuments: AssetDocument[];
	onSelectionChanged: (selectedDocuments: AssetDocument[]) => void;
	isLoading: boolean;
}

export const DocumentsTable: React.FC<Props> = ({
	allDocuments,
	assetDocuments,
	onSelectionChanged,
	isLoading,
}) => {
	const [filterInput, setFilterInput] = useState("");
	const [error, setError] = useState<string>("");
	const gridApiRef = useRef<GridApi | null>(null);

	const handlerFilter = (e: React.ChangeEvent<HTMLInputElement>) => {
		setFilterInput(e.target.value);
	};

	const sortRows = useCallback(() => {
		if (gridApiRef.current) {
			gridApiRef.current.applyColumnState({
				state: [{ colId: "DocumentName", sort: "asc", sortIndex: 0 }],
				defaultState: { sort: null },
			});
		}
	}, []);

	const loadingOverlayComponent = useMemo(() => LazyLoading, []);

	const rows = useMemo(() => {
		return allDocuments.map((document: AssetDocument) => ({
			...document,
			isSelected: assetDocuments.some(
				(assetDoc) => assetDoc.DocumentName === document.DocumentName,
			),
		}));
	}, [allDocuments, assetDocuments]);

	const columns: ColDef[] = useMemo(
		() => [
			{
				field: "DocumentName",
				headerName: "Document",
				minWidth: 120,
				maxWidth: 120,
				flex: 2,
				sortable: true,
				sortIndex: 1,
				comparator: (valueA, valueB, nodeA, nodeB) => {
					if (nodeA.isSelected() && !nodeB.isSelected()) {
						return -1;
					} else if (!nodeA.isSelected() && nodeB.isSelected()) {
						return 1;
					} else {
						return (valueA || "").localeCompare(valueB || "");
					}
				},
			},
			{
				field: "Title",
				headerName: "Title",
				flex: 4,
				sortable: true,
				sortIndex: 1,
				comparator: (valueA, valueB, nodeA, nodeB) => {
					if (nodeA.isSelected() && !nodeB.isSelected()) {
						return -1;
					} else if (!nodeA.isSelected() && nodeB.isSelected()) {
						return 1;
					} else {
						return (valueA || "").localeCompare(valueB || "");
					}
				},
			},
			{
				field: "AssetName",
				headerName: "Asset",
				minWidth: 140,
				maxWidth: 140,
				flex: 3,
				sortable: true,
				sortIndex: 1,
				comparator: (valueA, valueB, nodeA, nodeB) => {
					if (nodeA.isSelected() && !nodeB.isSelected()) {
						return -1;
					} else if (!nodeA.isSelected() && nodeB.isSelected()) {
						return 1;
					} else {
						return (valueA || "").localeCompare(valueB || "");
					}
				},
			},
		],
		[],
	);

	const onGridReady = useCallback((event: GridReadyEvent) => {
		gridApiRef.current = event.api;
	}, []);

	const onFirstDataRendered = useCallback(
		(params: FirstDataRenderedEvent) => {
			if (assetDocuments && assetDocuments.length > 0) {
				params.api.forEachNode((node) => {
					const isSelected = assetDocuments.some(
						(document) => document.DocumentName === node.data.DocumentName,
					);
					if (isSelected) {
						node.setSelected(true);
					}
				});
			}
			sortRows();
		},
		[assetDocuments, sortRows],
	);

	const onAgGridSelectionChanged = useCallback(
		(event: SelectionChangedEvent) => {
			const selectedRows = event.api.getSelectedRows();
			onSelectionChanged(selectedRows);
			sortRows();
			event.api.refreshClientSideRowModel("sort");
		},
		[onSelectionChanged, sortRows],
	);

	useEffect(() => {
		if (gridApiRef.current) {
			gridApiRef.current.setGridOption("quickFilterText", filterInput.trim());
		}
	}, [filterInput]);

	useEffect(() => {
		if (gridApiRef.current && rows.length > 0) {
			gridApiRef.current.deselectAll();
			gridApiRef.current.forEachNode((node) => {
				if (
					assetDocuments.some(
						(docToSelect) =>
							docToSelect.DocumentName === node.data.DocumentName,
					)
				) {
					node.setSelected(true);
				}
			});
			sortRows();
			gridApiRef.current.refreshClientSideRowModel("sort");
		}
	}, [assetDocuments, rows, sortRows]);

	return (
		<>
			<Grid2
				container
				style={{ padding: "0 0 10px", justifyContent: "space-between" }}
			>
				<Grid2 size={{ xs: 12 }}>
					<TextField
						label="Search"
						variant="outlined"
						value={filterInput}
						onChange={handlerFilter}
						margin="dense"
						fullWidth
					/>
				</Grid2>
			</Grid2>
			<div style={{ width: "100%", height: "60vh" }}>
				<Grid2 style={{ width: "100%", height: "60vh" }}>
					<AgGridReact
						getRowId={(params) => params.data.DocumentName}
						rowHeight={30}
						headerHeight={25}
						animateRows={true}
						onGridReady={onGridReady}
						rowData={rows}
						columnDefs={columns}
						defaultColDef={globalDefaultColDef}
						onFirstDataRendered={onFirstDataRendered}
						loadingOverlayComponent={loadingOverlayComponent}
						rowSelection={{
							mode: "multiRow",
							checkboxes: true,
							headerCheckbox: true,
							enableClickSelection: true,
							hideDisabledCheckboxes: false,
							enableSelectionWithoutKeys: true,
						}}
						onSelectionChanged={onAgGridSelectionChanged}
						suppressContextMenu={true}
						loading={isLoading}
					/>
				</Grid2>
			</div>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
